// ── Domain types ──────────────────────────────────────────────────────────────

export type SupportTicketStatus = "Pending" | "Synced" | "Failed";

export type SupportTicketTopic =
  | "setup-verification"
  | "account-settings"
  | "payments"
  | "payouts"
  | "troubleshooting"
  | "referral-program"
  | "other";

/** Lightweight row for the list/table view. */
export interface SupportTicketRow {
  id: string;
  reference: string;
  topic: string;
  status: SupportTicketStatus;
  businessId: string | null;
  userId: string;
  createdAt: string | null;
}

/**
 * What one retry of a request's HubSpot sync concluded.
 *
 * Mirrors the backend outcome rather than collapsing to success/failure: the
 * sheet has to tell an operator why nothing happened, and "already synced",
 * "deleted", and "HubSpot rejected it again" call for three different reactions.
 */
export type RetrySyncOutcome =
  | "synced"
  | "already-synced"
  | "missing"
  | "unreadable"
  | "unrecorded"
  | "failed";

export interface RetrySyncResult {
  outcome: RetrySyncOutcome;
  hubspotTicketId: string | null;
  message: string | null;
  /** Set for a failure: whether pressing the button again could work. */
  retryable: boolean | null;
}

/** Human labels for ticket topics; falls back to the raw id when unknown. */
const TOPIC_LABELS: Record<SupportTicketTopic, string> = {
  "setup-verification": "Setup & verification",
  "account-settings": "Account settings",
  payments: "Payments",
  payouts: "Payouts",
  troubleshooting: "Troubleshooting",
  "referral-program": "Referral program",
  other: "Something else",
};

export function topicLabel(topic: string): string {
  return (
    TOPIC_LABELS[topic as SupportTicketTopic] ??
    topic.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

// ── Filters ───────────────────────────────────────────────────────────────────

export interface SupportFilters {
  /** Server-side: DynamoDB `contains` filter on reference. */
  searchQuery: string;
  /** Server-side: narrows to one merchant through the businessId index. */
  businessId: string;
  /** Client-side: applied to the cached list. */
  statuses: SupportTicketStatus[];
  /** Client-side: applied to the cached list. */
  topics: string[];
}

export function defaultFilters(): SupportFilters {
  return { searchQuery: "", businessId: "", statuses: [], topics: [] };
}

const CLIENT_ONLY_FILTER_KEYS: (keyof SupportFilters)[] = [
  "statuses",
  "topics",
];

/** True when every key in the patch is a client-side-only filter key. */
export function isClientOnlyPatch(patch: Partial<SupportFilters>): boolean {
  const keys = Object.keys(patch) as (keyof SupportFilters)[];
  return (
    keys.length > 0 && keys.every((k) => CLIENT_ONLY_FILTER_KEYS.includes(k))
  );
}

// ── Selection set ─────────────────────────────────────────────────────────────

export const SUPPORT_SELECTION_SET = [
  "id",
  "reference",
  "topic",
  "status",
  "businessId",
  "userId",
  "createdAt",
] as const;
